import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { isPrivatePath } from "../lib/privateRoutes";

/**
 * RobotsPolicy — emits <meta name="robots" content="noindex,nofollow"> on the
 * private surface, and removes it everywhere else.
 *
 * WHY THIS IS ONE COMPONENT AND NOT A PROP ON 24 PAGES
 * The private routes are spread across two dozen page components, most of
 * them dashboard code this change is not allowed to touch. Mounting one
 * location-aware component inside the Router covers all of them, keeps the
 * policy in a single readable place, and means a NEW protected route is
 * covered the moment its path is added to PRIVATE_ROUTES — rather than
 * whenever someone remembers to add a tag.
 *
 * >>> READ THIS BEFORE TRUSTING IT ALONE. <<<
 * None of the private routes is prerendered (they are in NEVER_PRERENDER —
 * a prerendered /dashboard would publish app chrome to crawlers), so every
 * one of them serves the raw app-shell.html. That means THIS TAG ONLY
 * EXISTS AFTER JAVASCRIPT RUNS. Googlebot renders JS and will see it; a
 * crawler that does not execute JS never will.
 *
 * The JS-independent layer is an `X-Robots-Tag: noindex` response header set
 * at nginx for the same paths — see the deploy handoff. This component is
 * the layer that works without server access, and the belt to that braces.
 *
 * `nofollow` is included deliberately: these pages' links point further into
 * the app, and there is nothing behind a login worth spending crawl budget
 * discovering.
 */
export default function RobotsPolicy() {
  const { pathname } = useLocation();

  useEffect(() => {
    const OWNED = "data-tadlo-robots";
    const existing = document.head.querySelector(`meta[${OWNED}]`);

    if (!isPrivatePath(pathname)) {
      // Public page: remove OUR tag if the previous route left one behind.
      // Only ever removes a node this component created — a hand-written
      // robots meta in index.html is not ours to delete.
      if (existing) existing.remove();
      return undefined;
    }

    if (!existing) {
      const el = document.createElement("meta");
      el.setAttribute("name", "robots");
      el.setAttribute("content", "noindex,nofollow");
      el.setAttribute(OWNED, "1");
      document.head.appendChild(el);
    }

    return undefined;
  }, [pathname]);

  return null;
}
